import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CapabilityRequired,
  CatalogChanged,
  ErrorCode,
  InvalidFormField,
  ItemIdentityConflict,
  ItemProductTaken,
  MarketNotLinked,
  ScanCandidateUnknown,
  ScanNotFound,
  ScanNotReviewable,
  ScanStartRefused,
  StoreNotFound,
  StreamRefused,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { StoreCustomer } from "../model/card";
import { parse } from "../model/common";
import { QuickCharge } from "../model/customer-cart";
import {
  MyScan,
  Scan,
  ScanConfirmation,
  type ScanConfirmOptions,
  type ScanCorrections,
  ScanEdit,
  type ScanEditOptions,
  type ScanListQuery,
  ScanMarketRefresh,
  ScanReviewGroup,
  type ScanStart,
  ScanStreamEvent,
} from "../model/scan";
import { forward, type Result } from "../result";
import { forgetAttempt, pendingAttempts, rememberAttempt } from "../scan-attempts";
import type { Transport } from "../transport";

const ScanEnvelope = z.object({ scan: Scan });

const waitsForPhoto = (event: ScanStreamEvent) =>
  event.type === "scan.completed" && event.data.recognitionOutcome === "needs_photo";

/** The shop does not take customers at the counter, so a customer cannot scan there. */
function asCounterClosed(e: ValidationError): CapabilityRequired | null {
  return e.code === ErrorCode.CapabilityRequired ? new CapabilityRequired(e.incorrectValue, e.detailedDesc, e) : null;
}

/** A refusal the caller can act on, from a stream that would not open. */
function startRefusal(error: unknown): ScanStartRefused | CapabilityRequired | null {
  if (!(error instanceof StreamRefused)) return null;
  for (const e of error.errors) {
    const refused = e instanceof ValidationError ? (ScanStartRefused.from(e) ?? asCounterClosed(e)) : null;
    if (refused) return refused;
  }
  return null;
}
const ReviewQueueResponse = z.object({ groups: z.array(ScanReviewGroup) });
const CustomersResponse = z.object({ customers: z.array(StoreCustomer) });

/**
 * `/shop/:shopId/scan`. `shopId` is an entry id: a store id, or `loc_<id>` to
 * name the counter the scan happened at.
 */
export class ScansApi {
  constructor(private readonly t: Transport) {}

  /**
   * Runs the pipeline and returns the finished scan, 10-30 s later. A member
   * of the shop scans as OWNER; anyone else signed in scans as a CUSTOMER of it.
   */
  async start(
    ctx: ApiContext,
    shopId: string,
    images: ScanStart,
  ): Promise<Result<Scan, ApiNotAuthorized | StoreNotFound | ScanStartRefused | CapabilityRequired>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/scan/start`, { body: images });
    return forward(
      r,
      (j) => parse(Scan, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(shopId, e);
          const refused = ScanStartRefused.from(e) ?? asCounterClosed(e);
          if (refused) return refused;
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * The same scan, reported stage by stage instead of after ~20 seconds of
   * silence.
   *
   * `start` and this hit one endpoint — the backend content-negotiates on
   * `Accept` — so the choice is only whether the caller wants progress.
   *
   * Yields a typed `ScanStreamEvent` per frame and ends when the pipeline does.
   * Two things make it unlike every other method here:
   *
   * - **A pipeline failure is a `scan.error` frame, not a thrown error.** By the
   *   time one happens the response headers are long gone. Only a stream that
   *   could not be opened at all throws: `ScanStartRefused` for a request the
   *   caller can fix (a bad barcode, a missing photo), never recovered.
   * - **A barcode-only scan may complete as `needs_photo`.** Its attempt stays
   *   remembered: the photo is sent under the same `attemptId`.
   * - **Aborting stops the listening, not the scan.** The backend links the
   *   ShopScan before the pipeline runs, so it completes and persists either
   *   way; a caller that abandons a stream can still find the scan afterwards.
   *
   * A frame this client cannot parse is skipped rather than ending the stream —
   * a new event type added by the backend must not break an older client
   * mid-scan.
   */
  async *startStream(
    ctx: ApiContext,
    shopId: string,
    scan: ScanStart,
    opts: { signal?: AbortSignal } = {},
  ): AsyncGenerator<ScanStreamEvent> {
    if (scan.enableNoMatch && scan.attemptId) rememberAttempt(ctx, shopId, scan.attemptId);
    let terminal = false;
    try {
      for await (const frame of this.t.stream(ctx, "POST", `/shop/${shopId}/scan/start`, {
        body: scan,
        signal: opts.signal,
      })) {
        const parsed = ScanStreamEvent.safeParse({ type: frame.event, data: frame.data });
        if (!parsed.success) continue;
        const event = parsed.data;
        if (event.type === "scan.started" && scan.enableNoMatch && scan.attemptId) {
          rememberAttempt(ctx, shopId, scan.attemptId, event.data.scanId);
        }
        if (event.type === "scan.error" && event.data.terminal === false) break;
        terminal = event.type === "scan.completed" || event.type === "scan.error";
        if (terminal && scan.attemptId && !waitsForPhoto(event)) forgetAttempt(ctx, shopId, scan.attemptId);
        yield event;
      }
    } catch (error) {
      const refused = startRefusal(error);
      if (refused) {
        if (scan.attemptId) forgetAttempt(ctx, shopId, scan.attemptId);
        throw refused;
      }
      if (!scan.enableNoMatch || !scan.attemptId || opts.signal?.aborted) throw error;
      // Refused before its first frame: the backend started nothing this attempt could be found by.
      if (error instanceof StreamRefused) throw error;
    }
    if (!terminal && scan.enableNoMatch && scan.attemptId && !opts.signal?.aborted) {
      yield* this.recoverStream(ctx, shopId, scan.attemptId, opts);
    }
  }

  pendingAttempts(ctx: ApiContext, shopId: string) {
    return pendingAttempts(ctx, shopId);
  }

  /** The scan an attempt started, as it stands now. */
  async attempt(
    ctx: ApiContext,
    shopId: string,
    attemptId: string,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<Scan, ApiNotAuthorized | StoreNotFound | ScanNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/scan/attempt/${attemptId}`, { query: opts });
    return forward(
      r,
      (j) => parse(ScanEnvelope, j).scan,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "scanId") return new ScanNotFound(attemptId, e);
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Observe an existing attempt. Never POST again after losing a response. */
  async *recoverStream(
    ctx: ApiContext,
    shopId: string,
    attemptId: string,
    opts: { signal?: AbortSignal } = {},
  ): AsyncGenerator<ScanStreamEvent> {
    while (!opts.signal?.aborted) {
      const r = await this.attempt(ctx, shopId, attemptId, { enableNoMatch: true });
      if (!r.ok) throw r.errors[0];
      const scan = r.value;
      rememberAttempt(ctx, shopId, attemptId, scan.id);
      yield { type: "scan.started", data: { stage: "preparation", scanId: scan.id } };
      if (scan.scanProgress === "scan.completed") {
        if (scan.recognitionOutcome !== "needs_photo") forgetAttempt(ctx, shopId, attemptId);
        yield { type: "scan.completed", data: scan };
        return;
      }
      if (scan.scanProgress === "scan.error") {
        forgetAttempt(ctx, shopId, attemptId);
        yield {
          type: "scan.error",
          data: { stage: "persistence", error: "The scan failed. Upload again to start a new attempt." },
        };
        return;
      }
      await new Promise<void>((resolve) => {
        const finish = () => {
          clearTimeout(timer);
          opts.signal?.removeEventListener("abort", finish);
          resolve();
        };
        const timer = setTimeout(finish, 1500);
        opts.signal?.addEventListener("abort", finish, { once: true });
      });
    }
  }

  async list(
    ctx: ApiContext,
    shopId: string,
    query: ScanListQuery = {},
  ): Promise<Result<Scan[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/scan/all`, {
      query: { ...query, updatedAfter: query.updatedAfter?.toISOString() },
    });
    return forward(
      r,
      (j) => parse(z.array(Scan), j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The review queue, grouped by the identity each pending scan resolves to. */
  async reviewQueue(
    ctx: ApiContext,
    shopId: string,
    locationId?: string,
    /** Whose scans: the shop's own when omitted. */
    kind?: "owner" | "customer" | "all",
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<ScanReviewGroup[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/scan/review`, { query: { locationId, kind, ...opts } });
    return forward(
      r,
      (j) => parse(ReviewQueueResponse, j).groups,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The caller's own scans in the shop, whoever they are, each with the state the buyer sees. */
  async my(
    ctx: ApiContext,
    shopId: string,
    updatedAfter?: Date,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<MyScan[], ApiNotAuthorized | StoreNotFound | CapabilityRequired>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/scan/my`, {
      query: { updatedAfter: updatedAfter?.toISOString(), ...opts },
    });
    return forward(
      r,
      (j) => parse(z.array(MyScan), j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          const closed = asCounterClosed(e);
          if (closed) return closed;
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async get(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<Scan, ApiNotAuthorized | StoreNotFound | ScanNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/scan/${scanId}`, { query: opts });
    return forward(
      r,
      (j) => parse(ScanEnvelope, j).scan,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId") return new ScanNotFound(scanId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Corrects a pending scan's reading. Merged over the reading in force; the model's own output is kept. */
  async edit(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
    corrections: ScanCorrections,
    options: ScanEditOptions = {},
  ): Promise<Result<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable>> {
    const r = await this.t.patch(ctx, `/shop/${shopId}/scan/${scanId}`, { body: { corrections, ...options } });
    return forward(
      r,
      (j) => parse(ScanEdit, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Makes one of the scan's candidates the pick, by the id in its `product`.
   * No lookup follows: the candidate's own prices are used, and the scan is
   * re-matched against the shop's stock.
   */
  async pick(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
    productId: string,
  ): Promise<
    Result<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown>
  > {
    const r = await this.t.patch(ctx, `/shop/${shopId}/scan/${scanId}`, { body: { productId } });
    return forward(
      r,
      (j) => parse(ScanEdit, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          if (e.incorrectValue === "productId") return new ScanCandidateUnknown(productId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Settles a catalogue conflict: the scan becomes one of `catalog.candidates`
   * and is priced like a catalogue hit. Picking a pricing candidate does not.
   */
  async pickCatalog(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
    catalogProductId: string,
  ): Promise<
    Result<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown>
  > {
    const r = await this.t.patch(ctx, `/shop/${shopId}/scan/${scanId}`, { body: { catalogProductId } });
    return forward(
      r,
      (j) => parse(ScanEdit, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          if (e.incorrectValue === "catalogProductId") return new ScanCandidateUnknown(catalogProductId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Confirms a scan as a card: the only path that creates one. */
  async confirm(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
    options: ScanConfirmOptions = {},
  ): Promise<
    Result<
      ScanConfirmation,
      | ApiNotAuthorized
      | StoreNotFound
      | ScanNotFound
      | ScanNotReviewable
      | CatalogChanged
      | ItemIdentityConflict
      | ItemProductTaken
      | InvalidFormField
    >
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/scan/${scanId}/confirm`, { body: options });
    return forward(
      r,
      (j) => parse(ScanConfirmation, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === CatalogChanged.code) return new CatalogChanged(e.detailedDesc, e);
          if (e.code === ItemIdentityConflict.code) {
            const fields = Array.isArray(e.details.fields) ? e.details.fields.map(String) : [];
            return new ItemIdentityConflict(fields, e.detailedDesc, e);
          }
          if (e.code === ItemProductTaken.code) {
            const itemId = typeof e.details.itemId === "string" ? e.details.itemId : null;
            return new ItemProductTaken(itemId, e.detailedDesc, e);
          }
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Confirms a group of scans as one card: ten copies confirm once into a lot of ten. `ScanNotFound` names the whole group when one is missing. */
  async confirmMany(
    ctx: ApiContext,
    shopId: string,
    scanIds: string[],
    options: Omit<ScanConfirmOptions, "linkItemId" | "adoptScanName"> = {},
  ): Promise<
    Result<
      ScanConfirmation,
      ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | CatalogChanged | InvalidFormField
    >
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/scan/confirm-many`, { body: { ...options, scanIds } });
    return forward(
      r,
      (j) => parse(ScanConfirmation, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === CatalogChanged.code) return new CatalogChanged(e.detailedDesc, e);
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist)
            return new ScanNotFound(scanIds.join(","), e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Adds a counter (POS) customer, whom staff scan and charge for. */
  async createCustomer(
    ctx: ApiContext,
    shopId: string,
    name: string,
  ): Promise<Result<StoreCustomer, ApiNotAuthorized | StoreNotFound | InvalidFormField>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/customers`, { body: { name } });
    return forward(
      r,
      (j) => parse(StoreCustomer, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * A sale with no cards to scan: the agreed total in minor units and, optionally,
   * who paid. Returns the one-line cart, which Tap to Pay then checks out.
   */
  async quickCharge(
    ctx: ApiContext,
    shopId: string,
    amountCents: number,
    customerName?: string,
  ): Promise<Result<QuickCharge, ApiNotAuthorized | StoreNotFound | InvalidFormField>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/quick-charge`, { body: { amountCents, customerName } });
    return forward(
      r,
      (j) => parse(QuickCharge, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** The shop's customers who currently hold a valid, unexpired scan in it; with `loc_<id>`, at that location. */
  async customers(
    ctx: ApiContext,
    shopId: string,
    opts: { enableNoMatch?: boolean } = {},
  ): Promise<Result<StoreCustomer[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/customers`, { query: opts });
    return forward(
      r,
      (j) => parse(CustomersResponse, j).customers,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Asks the pricing source for the shared estimate of a pending sealed scan's
   * product, past the price cache. Within a minute of the last retrieval the
   * outcome is `cooldown` and nothing is asked. The asking price is untouched.
   */
  async refreshMarketPrice(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
  ): Promise<
    Result<ScanMarketRefresh, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | MarketNotLinked>
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/scan/${scanId}/market-price/refresh`);
    return forward(
      r,
      (j) => parse(ScanMarketRefresh, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.code === ErrorCode.NotLinked) return new MarketNotLinked(e.detailedDesc, e);
          if (e.incorrectValue === "scanId" && e.code === ErrorCode.DoesntExist) return new ScanNotFound(scanId, e);
          if (e.incorrectValue === "scanId") return new ScanNotReviewable(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Rejects a scan. No item is created and nothing is cleaned up. */
  async reject(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | ScanNotFound>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/scan/${scanId}/reject`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId") return new ScanNotFound(scanId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Deletes a scan. Open to the shop's staff and to the customer who made it. */
  async remove(
    ctx: ApiContext,
    shopId: string,
    scanId: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | ScanNotFound>> {
    const r = await this.t.delete(ctx, `/shop/${shopId}/scan/${scanId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "scanId") return new ScanNotFound(scanId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
